import React, { useState } from 'react';
import '../css/roundIconButton.css';

interface OptionItem {
  id: string;
  icon?: string;
  iconAlt?: string;
  label?: string;
  onClick?: () => void;
}

interface RoundIconButtonProps {
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  options?: OptionItem[];
  maxPerRow?: number;
  children?: React.ReactNode;
}

const RoundIconButton: React.FC<RoundIconButtonProps> = ({
  onClick,
  className,
  disabled = false,
  options,
  maxPerRow,
  children
}) => { //NOSONAR
  const [isOpen, setIsOpen] = useState(false);
  const hasOptionsBar = !!(options && options.length > 0) || !!children;
  const hasLabels = options?.some(opt => opt.label);

  const BAR_MAX_WIDTH = 632;
const ITEM_EST_WIDTH = 160;
const autoMaxPerRow = hasLabels
  ? maxPerRow ?? Math.floor(BAR_MAX_WIDTH / ITEM_EST_WIDTH)
  : undefined;

// Calculate if content width exceeds BAR_MAX_WIDTH
const estimatedContentWidth = options ? options.length * ITEM_EST_WIDTH : 0;
const needsExpansion = estimatedContentWidth > BAR_MAX_WIDTH;

const isMultiRow = !!(needsExpansion && autoMaxPerRow && options && options.length > autoMaxPerRow);

  const handleClick = () => {
    if (hasOptionsBar) {
      setIsOpen(!isOpen);
    }
    onClick?.();
  };

  const handleOptionClick = (optionOnClick?: () => void) => {
    optionOnClick?.();
    setIsOpen(false);
  };

  const button = (
    <button
      type="button"
      onClick={handleClick}
      className={`scl--web-component-round-icon-button ${disabled ? 'disabled' : ''} ${className || ''}`}
      disabled={disabled}
    >
      <svg
        className={`scl--web-component-round-icon-button-icon ${isOpen ? 'rotated' : ''}`}
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line x1="5" y1="15" x2="25" y2="15" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="15" y1="5" x2="15" y2="25" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </button>
  );

  if (!hasOptionsBar) {
    return button;
  }

  return (
    <div className="scl--web-component-round-icon-button-wrapper">
      <div className={`scl--web-component-round-icon-button-options-bar ${isOpen ? 'visible' : ''} ${hasLabels ? 'has-labels' : ''} ${isMultiRow ? 'multi-row' : ''}`}>
        {isMultiRow && options ? (
          <div className="scl--web-component-round-icon-button-options-multi-row">
            {(() => {
              const rows: OptionItem[][] = [];
              for (let i = 0; i < options.length; i += autoMaxPerRow!) { //NOSONAR
                rows.push(options.slice(i, i + autoMaxPerRow!)); //NOSONAR
              }
              return rows.map((row, rowIndex) => (
                <React.Fragment key={rowIndex} //NOSONAR
                >
                  {rowIndex > 0 && (
                    <div className="scl--web-component-round-icon-button-options-divider" />
                  )}
                  <div className="scl--web-component-round-icon-button-options-inner">
                    {row.map((option) => (
                      <div //NOSONAR
                        key={option.id}
                        className={`scl--web-component-round-icon-button-option-item ${option.label ? 'with-label' : ''}`}
                        onClick={() => handleOptionClick(option.onClick)} //NOSONAR
                      >
                        {option.label ? (
                          <div className="scl--web-component-round-icon-button-option-icon-label">
                            <div className="scl--web-component-round-icon-button-option-icon">
                              <img src={option.icon} alt={option.iconAlt} />
                            </div>
                            <span className="scl--web-component-round-icon-button-option-label">
                              {option.label}
                            </span>
                          </div>
                        ) : (
                          <div className="scl--web-component-round-icon-button-option-icon">
                            <img src={option.icon} alt={option.iconAlt} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              ));
            })()}
          </div>
        ) : (
          <div className="scl--web-component-round-icon-button-options-inner">
            {options && options.length > 0 ? (
              options.map((option) => (
                <div //NOSONAR
                  key={option.id}
                  className={`scl--web-component-round-icon-button-option-item ${option.label ? 'with-label' : ''}`}
                  onClick={() => handleOptionClick(option.onClick)}
                >
                  {option.label ? (
                    <div className="scl--web-component-round-icon-button-option-icon-label">
                      <div className="scl--web-component-round-icon-button-option-icon">
                        <img src={option.icon} alt={option.iconAlt} />
                      </div>
                      <span className="scl--web-component-round-icon-button-option-label">
                        {option.label}
                      </span>
                    </div>
                  ) : (
                    <div className="scl--web-component-round-icon-button-option-icon">
                      <img src={option.icon} alt={option.iconAlt} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              children
            )}
          </div>
        )}
      </div>
      {button}
    </div>
  );
};

export default RoundIconButton;